import { MenuItem } from '@/types/menu';
import { RouteConfig } from '@/types/route';
import { ROUTES_CONFIG } from '@/config/routesConfig';

/**
 * 将菜单列表转换为树形结构
 */
export const buildMenuTree = (menuItems: MenuItem[]): MenuItem[] => {
  const menuMap = new Map<number, MenuItem>();
  const rootMenus: MenuItem[] = [];

  // 首先建立映射关系
  menuItems.forEach((menu) => {
    menuMap.set(menu.menuId, { ...menu, children: [] });
  });

  // 构建树形结构
  menuItems.forEach((menu) => {
    const menuWithChildren = menuMap.get(menu.menuId)!;
    if (menu.parentId === 0) {
      rootMenus.push(menuWithChildren);
    } else {
      const parentMenu = menuMap.get(menu.parentId);
      if (parentMenu) {
        if (!parentMenu.children) {
          parentMenu.children = [];
        }
        parentMenu.children.push(menuWithChildren);
      }
    }
  });

  return rootMenus;
};

/**
 * 根据后端菜单数据过滤和重组路由配置
 */
export const buildRoutesFromMenu = (menuItems: MenuItem[]): RouteConfig[] => {
  // 构建一个 URL 到路由配置的映射
  const routeMap = new Map<string, RouteConfig>();

  // 只处理需要权限的路由
  ROUTES_CONFIG.filter((route) => !route.noPermissionRequired).forEach(
    (route) => {
      routeMap.set(route.path, route);
    }
  );

  const menuTree = buildMenuTree(menuItems);
  const result: RouteConfig[] = [];

  // 递归处理菜单树
  const processMenu = (menu: MenuItem): RouteConfig | null => {
    // 跳过隐藏的菜单
    if (menu.visible === '1') return null;

    // 处理子菜单
    const childRoutes: RouteConfig[] = [];
    if (menu.children?.length) {
      menu.children.forEach((child) => {
        const childRoute = processMenu(child);
        if (childRoute) {
          childRoutes.push(childRoute);
        }
      });
    }

    // 如果是父级菜单（URL为#）
    if (menu.url === '#') {
      if (childRoutes.length > 0) {
        return {
          path: menu.url,
          label: menu.menuName,
          children: childRoutes,
        };
      }
      return null;
    }

    // 如果是实际路由
    const routeConfig = routeMap.get(menu.url);
    if (!routeConfig) return null;

    const route: RouteConfig = {
      ...routeConfig,
      label: menu.menuName,
      perms: menu.perms,
    };

    if (childRoutes.length > 0) {
      route.children = childRoutes;
    }

    return route;
  };

  // 处理所有根菜单
  menuTree.forEach((menu) => {
    const route = processMenu(menu);
    if (route) {
      result.push(route);
    }
  });

  return result;
};
